<template>
  <v-dialog
    :model-value="modelValue"
    max-width="900px"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="dialog-card">
      <v-card-title class="pa-0 dialog-header">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>{{ t('lead-timeline.email-activity.edit-next-email') }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              @click="closeDialog"
              :disabled="saving"
            >
              <v-icon size="large">mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>

      <v-card-text class="mt-4 dialog-body">
        <!-- Subject Field -->
        <v-text-field
          v-model="editedSubject"
          :label="t('lead-timeline.email-activity.subject')"
          variant="outlined"
          class="mb-4"
        />

        <!-- Email Body Section -->
        <div class="mt-4 mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <h5 class="text-h6 mb-0">
              {{ t('lead-timeline.email-activity.email-body') }}
            </h5>
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :disabled="enhancing || !props.message?.mjmlBody"
              @click="handleEnhanceEmail"
            >
              <v-icon
                start
                :class="{ 'icon-spin': enhancing }"
              >
                {{ enhancing ? 'mdi-autorenew' : 'mdi-robot' }}
              </v-icon>
              {{ t('lead-timeline.email-activity.enhance-email') }}
            </v-btn>
          </div>

          <!-- Email Body Editor -->
          <v-textarea
            v-model="editedTextBody"
            :label="t('lead-timeline.email-activity.email-body')"
            variant="outlined"
            rows="10"
            class="mb-4"
            :error="textBodyError"
            :error-messages="textBodyError ? t('lead-timeline.email-activity.body-error') : ''"
          />
        </div>

        <!-- Attachments Section -->
        <div class="mt-4">
          <div class="d-flex align-center mb-2">
            <span class="text-subtitle-2">{{ t('lead-timeline.email-activity.attachments') }}</span>
            <v-btn
              size="small"
              variant="text"
              color="primary"
              class="ml-2"
              @click="handleAddAttachmentClick"
            >
              <v-icon start>mdi-paperclip</v-icon>
              {{ t('lead-timeline.email-activity.add-attachment') }}
            </v-btn>
            <input
              ref="fileInput"
              type="file"
              style="display: none"
              multiple
              :accept="acceptString"
              @change="handleFileSelect"
            />
          </div>
          <div class="text-caption text-grey mb-2">
            {{ t('lead-timeline.email-activity.max-file-size') }}
            <br />
            {{ t('lead-timeline.email-activity.allowed-file-types') }}
          </div>
          <!-- Size Display -->
          <div
            class="text-caption mb-2"
            :class="{
              'text-error': exceedsTotalSizeLimit,
              'text-warning': totalSize > maxTotalSize * 0.8 && !exceedsTotalSizeLimit,
              'text-grey': !exceedsTotalSizeLimit && totalSize <= maxTotalSize * 0.8,
            }"
          >
            <v-icon
              size="small"
              :color="
                exceedsTotalSizeLimit
                  ? 'error'
                  : totalSize > maxTotalSize * 0.8
                    ? 'warning'
                    : 'grey'
              "
              class="mr-1"
            >
              {{ exceedsTotalSizeLimit ? 'mdi-alert-circle' : 'mdi-information' }}
            </v-icon>
            {{ t('lead-timeline.email-activity.total-size') }}:
            {{ sizeDisplayText }}
          </div>

          <!-- Attachment List -->
          <v-chip-group
            v-if="sortedEditedAttachments.length > 0"
            column
          >
            <v-chip
              v-for="(attachment, index) in sortedEditedAttachments"
              :key="attachment.id || `new-${index}`"
              closable
              size="small"
              @click:close="removeAttachment(index)"
            >
              <v-icon
                start
                size="small"
                >mdi-file</v-icon
              >
              {{ attachment.name }}
              <span class="text-caption ml-1">({{ formatFileSize(attachment.size) }})</span>
            </v-chip>
          </v-chip-group>
          <div
            v-else
            class="text-caption text-grey"
          >
            {{ t('lead-timeline.email-activity.no-attachments') }}
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer">
        <v-btn
          variant="text"
          @click="handleCancelClick"
          :disabled="saving"
        >
          {{ t('labels.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="success"
          variant="text"
          @click="handleSaveClick"
          :loading="saving"
          :disabled="!editedSubject || exceedsTotalSizeLimit || textBodyError"
        >
          {{ t('labels.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import * as Sentry from '@sentry/vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  allowedFileExtensions,
  allowedFileTypes,
  forbiddenFileExtensions,
  getAcceptString,
} from '@/lib/allowedFileTypes'
import { emailAgentPrompt } from '@/lib/emailPrompts'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import COMPANY from '@/modules/settings/companySettings/queries/CompanyData.gql'

import EDIT_EMAIL from '../queries/EditEmail.gql'
import UPDATE_NEXT_MESSAGE from '../queries/UpdateNextMessage.gql'

const MAX_TOTAL_SIZE = 10 * 1024 * 1024 // 10MB total (email body + attachments)
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB per file

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  message: {
    type: Object,
    default: null,
  },
  contactId: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const { t, locale } = useI18n()
const { mutate: editEmailMutation } = useMutation(EDIT_EMAIL)
const { mutate: updateNextMessageMutation, loading: mutationLoading } =
  useMutation(UPDATE_NEXT_MESSAGE)
const $tracking = inject('tracking')
const $auth = inject('auth')

// Load company data for metadata
const { result: companyResult, load: loadCompany } = useLazyQuery(COMPANY, {
  id: $auth?.user?.companyId,
})

const editedSubject = ref('')
const editedTextBody = ref('')
const editedAttachments = ref([])
const saving = ref(false)
const fileInput = ref(null)
const textBodyError = ref(false)
const enhancing = ref(false)

// Store original values for comparison
const originalTextBody = ref('')

// Debounced email body size calculation to avoid excessive Blob allocations on every keystroke
const debouncedEmailBodySize = ref(0)
let emailBodySizeDebounceTimer = null

/**
 * Format file size in bytes to human-readable format
 */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

/**
 * Calculate the accurate size of the email body using Blob
 * This is expensive, so it's only called after debounce
 */
const calculateEmailBodySize = () => {
  const textBody = editedTextBody.value || props.message?.textBody || ''
  const htmlBody = props.message?.htmlBody || ''
  // Calculate size in bytes (UTF-8 encoding)
  const textSize = new Blob([textBody]).size
  const htmlSize = new Blob([htmlBody]).size
  return textSize + htmlSize
}

/**
 * Update the debounced email body size after a delay
 * Prevents excessive Blob allocations on every keystroke
 */
const updateDebouncedEmailBodySize = () => {
  if (emailBodySizeDebounceTimer) {
    clearTimeout(emailBodySizeDebounceTimer)
  }
  emailBodySizeDebounceTimer = setTimeout(() => {
    debouncedEmailBodySize.value = calculateEmailBodySize()
  }, 300)
}

/**
 * Email body size - uses the debounced value for performance
 * The value is updated 300ms after the user stops typing
 */
const emailBodySize = computed(() => {
  return debouncedEmailBodySize.value
})

/**
 * Calculate total size of all attachments
 * Uses the size field which is always set:
 * - For new attachments: from File.size
 * - For existing attachments: from backend or estimated
 */
const attachmentsTotalSize = computed(() => {
  return editedAttachments.value.reduce((total, att) => {
    // Use size if available (even if 0, as it's a valid value)
    // New attachments have size from File object, existing from backend
    if (att.size != null && att.size !== undefined) {
      return total + att.size
    }
    // Fallback for existing attachments without size (legacy)
    if (att.content) {
      return total + Math.round((att.content.length * 3) / 4)
    }
    // For existing attachments without content or size, use a conservative estimate
    return total + 1024 * 100 // Estimate 100KB per existing attachment
  }, 0)
})

/**
 * Calculate total size (email body + attachments)
 */
const totalSize = computed(() => {
  return emailBodySize.value + attachmentsTotalSize.value
})

/**
 * Calculate remaining space
 */
const remainingSize = computed(() => {
  return Math.max(0, MAX_TOTAL_SIZE - totalSize.value)
})

/**
 * Check if total size exceeds limit
 */
const exceedsTotalSizeLimit = computed(() => {
  return totalSize.value > MAX_TOTAL_SIZE
})

/**
 * Get size display text with color indicator
 */
const sizeDisplayText = computed(() => {
  const total = formatFileSize(totalSize.value)
  const remaining = formatFileSize(remainingSize.value)
  const max = formatFileSize(MAX_TOTAL_SIZE)
  return `${total} / ${max} (${remaining} remaining)`
})

/**
 * Max total size for template access
 */
const maxTotalSize = computed(() => {
  return MAX_TOTAL_SIZE
})

/**
 * Get edited attachments (maintain array order)
 */
const sortedEditedAttachments = computed(() => {
  return [...editedAttachments.value]
})

/**
 * Accept string for file input
 */
const acceptString = computed(() => {
  return getAcceptString()
})

/**
 * Initialize dialog with current message data
 * Preserves existing attachments with their IDs, fileIds, and sizes
 */
const initializeDialog = () => {
  editedSubject.value = props.message?.subject || ''
  editedTextBody.value = props.message?.textBody || ''

  // Store original values for comparison
  originalTextBody.value = props.message?.textBody || ''

  // Calculate initial email body size immediately (once on load is fine)
  debouncedEmailBodySize.value = calculateEmailBodySize()

  // Validate fields to set initial error state
  validateFields()

  // Load existing attachments with all metadata
  editedAttachments.value = (props.message?.attachments || []).map((att) => {
    // Determine size: prioritize backend-provided size, then estimate from base64 if available
    let size = 0
    if (att.size != null && att.size !== undefined) {
      // Use backend-provided size (even if 0, as it's a valid value)
      size = att.size
    } else if (att.content) {
      // Estimate from base64 content if size not provided
      size = Math.round((att.content.length * 3) / 4)
    }

    return {
      id: att.id || null, // Preserve ID for existing attachments
      fileId: att.fileId || null, // Preserve fileId for existing attachments
      name: att.name,
      contentType: att.contentType,
      // For existing attachments, we may not have content (it's optional in response)
      // Only include content if it's available (for backward compatibility)
      content: att.content || null,
      // Use actual size from backend if available
      size: size,
      // Store url/path for display purposes if needed
      url: att.url || null,
      path: att.path || null,
    }
  })
}

/**
 * Reset dialog state
 */
const resetDialog = () => {
  editedSubject.value = ''
  editedTextBody.value = ''
  editedAttachments.value = []
  originalTextBody.value = ''
  textBodyError.value = false
  debouncedEmailBodySize.value = 0
  // Clear any pending debounce timer
  if (emailBodySizeDebounceTimer) {
    clearTimeout(emailBodySizeDebounceTimer)
    emailBodySizeDebounceTimer = null
  }
}

/**
 * Validate email body fields
 */
const validateFields = () => {
  textBodyError.value = false

  // Body must be filled
  if (!editedTextBody.value || editedTextBody.value.trim() === '') {
    textBodyError.value = true
  }
}

/**
 * Handle add attachment button click with tracking
 */
const handleAddAttachmentClick = () => {
  $tracking.event('Button', 'Click', 'Add Attachment')
  fileInput.value?.click()
}

/**
 * Handle cancel button click with tracking
 */
const handleCancelClick = () => {
  $tracking.event('Button', 'Click', 'Cancel Edit Email')
  closeDialog()
}

/**
 * Handle save button click with tracking
 */
const handleSaveClick = () => {
  $tracking.event('Button', 'Click', 'Save Next Email')
  saveMessage()
}

/**
 * Build metadata object from company data
 */
const buildMetadata = () => {
  const company = companyResult.value?.company
  const currentLocale = locale.value || 'en'

  if (!company) {
    // Return default metadata if company data is not available
    return {
      companyName: '',
      industry: '',
      region: '',
      slogan: '',
      country: '',
      countryCode: '',
      businessDescription: '',
      marketingGoal: '',
      language: currentLocale,
      tonality: 'professional',
      formality: 'neutral',
      perspective: 'neutral',
      targetGroup: '',
      salutation: '',
      farewell: '',
    }
  }

  return {
    companyName: company.name || '',
    industry: company.meta?.industry || '',
    region: company.regionName || '',
    slogan: company.slogan || '',
    country: company.country || '',
    countryCode: company.country?.toUpperCase() || '',
    businessDescription: company.meta?.businessDescription || '',
    marketingGoal: '',
    language: company.locale || currentLocale,
    tonality: 'professional',
    formality: 'neutral',
    perspective: 'neutral',
    targetGroup: '',
    salutation: company.ambassador?.salutation || '',
    farewell: '',
  }
}

/**
 * Handle enhance email button click
 */
const handleEnhanceEmail = async () => {
  if (!props.message?.mjmlBody) {
    showSnackbarMessage('error', t('lead-timeline.email-activity.no-mjml-body'))
    return
  }

  enhancing.value = true
  $tracking.event('Button', 'Click', 'Enhance Email')

  // Ensure company data is loaded
  if (!companyResult.value) {
    await loadCompany()
  }

  try {
    const result = await editEmailMutation({
      input: {
        MJMLBody: props.message.mjmlBody,
        plaintextBody: '',
        editedplaintextBody: '',
        error: emailAgentPrompt,
        isMJML: false,
        metadata: buildMetadata(),
      },
    })

    if (!result?.data?.editEmail) {
      showSnackbarMessage('error', t('lead-timeline.email-activity.enhance-email-error'))
      return
    }

    // Response is plain text, use it directly
    const plainText = result.data.editEmail

    // Update the edited text body with the enhanced content
    editedTextBody.value = plainText

    showSnackbarMessage('success', t('lead-timeline.email-activity.email-enhanced'))
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'EditEmailDialog',
        action: 'enhanceEmail',
      },
    })
    showSnackbarMessage('error', t('lead-timeline.email-activity.enhance-email-error'))
  } finally {
    enhancing.value = false
  }
}

/**
 * Close dialog
 */
const closeDialog = () => {
  emit('update:modelValue', false)
}

/**
 * Check if file type is forbidden
 */
const isForbiddenFileType = (file) => {
  const fileName = file.name.toLowerCase()
  return forbiddenFileExtensions.some((ext) => fileName.endsWith(ext.toLowerCase()))
}

/**
 * Check if file type is allowed
 */
const isAllowedFileType = (file) => {
  // First check if file is forbidden
  if (isForbiddenFileType(file)) {
    return false
  }

  // Check by MIME type
  if (file.type && allowedFileTypes.includes(file.type.toLowerCase())) {
    return true
  }

  // Check by file extension (fallback for files without MIME type)
  const fileName = file.name.toLowerCase()
  return allowedFileExtensions.some((ext) => fileName.endsWith(ext))
}

/**
 * Handle file selection from input
 */
const handleFileSelect = async (event) => {
  const files = event.target.files
  if (!files || files.length === 0) return

  for (const file of files) {
    // First check if file is forbidden (security check)
    if (isForbiddenFileType(file)) {
      showSnackbarMessage(
        'error',
        t('lead-timeline.email-activity.forbidden-file-type') ||
          `File "${file.name}" is forbidden for security reasons.`
      )
      continue
    }

    // Check if file type is allowed
    if (!isAllowedFileType(file)) {
      showSnackbarMessage(
        'error',
        t('lead-timeline.email-activity.invalid-file-type') ||
          `File "${file.name}" is not allowed. Only images, PDF, and documents are allowed.`
      )
      continue
    }

    // Check file size (max 10MB per file)
    if (file.size > MAX_FILE_SIZE) {
      showSnackbarMessage(
        'error',
        t('lead-timeline.email-activity.file-too-large') ||
          `File "${file.name}" is too large. Max size is ${formatFileSize(MAX_FILE_SIZE)}.`
      )
      continue
    }

    // Check if adding this file would exceed total size limit
    // Use accurate calculation here (not debounced) for precise validation
    const currentTotalSize = calculateEmailBodySize() + attachmentsTotalSize.value
    const newTotalSize = currentTotalSize + file.size
    if (newTotalSize > MAX_TOTAL_SIZE) {
      const remaining = MAX_TOTAL_SIZE - currentTotalSize
      showSnackbarMessage(
        'error',
        t('lead-timeline.email-activity.total-size-exceeded') ||
          `Adding "${file.name}" would exceed the total size limit. Remaining space: ${formatFileSize(remaining)}`
      )
      continue
    }

    // Store File object directly instead of converting to Base64 immediately
    // This reduces memory pressure (~33% savings) - Base64 conversion happens at submission
    editedAttachments.value.push({
      id: null, // New attachment
      fileId: null, // New attachment
      name: file.name,
      file: file, // Store the actual File object
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    })
  }

  // Reset the file input to allow selecting the same files again
  event.target.value = ''
}

/**
 * Convert File object to base64 string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Remove attachment from list
 * This will automatically trigger recalculation of:
 * - attachmentsTotalSize (computed)
 * - totalSize (computed, email body + attachments)
 * - remainingSize (computed)
 * - exceedsTotalSizeLimit (computed)
 * - sizeDisplayText (computed)
 */
const removeAttachment = (index) => {
  // Get attachments to find the correct one to remove
  const attachments = sortedEditedAttachments.value
  const attachmentToRemove = attachments[index]

  if (!attachmentToRemove) return

  // Remove by finding the attachment in the original array
  // Use a more reliable matching strategy
  const originalIndex = editedAttachments.value.findIndex((att) => {
    // For existing attachments with IDs, match by ID
    if (att.id && attachmentToRemove.id) {
      return att.id === attachmentToRemove.id
    }
    // For new attachments without IDs, match by name, contentType, and size
    if (!att.id && !attachmentToRemove.id) {
      return (
        att.name === attachmentToRemove.name &&
        att.contentType === attachmentToRemove.contentType &&
        att.size === attachmentToRemove.size
      )
    }
    return false
  })

  if (originalIndex !== -1) {
    editedAttachments.value.splice(originalIndex, 1)
  }
}

/**
 * Save next message with updated subject, body, and attachments
 */
const saveMessage = async () => {
  // Validate fields
  validateFields()
  if (textBodyError.value) {
    return
  }

  // Calculate accurate size for validation (not debounced)
  // This ensures correct validation even if save is clicked immediately after typing
  const accurateTotalSize = calculateEmailBodySize() + attachmentsTotalSize.value
  if (accurateTotalSize > MAX_TOTAL_SIZE) {
    showSnackbarMessage('error', t('lead-timeline.email-activity.total-size-exceeded'))
    return
  }

  saving.value = true
  try {
    // First, call EditEmail to get improved email using AI
    const originalMJMLBody = props.message?.mjmlBody || ''
    const originalPlaintextBody = originalTextBody.value || ''
    const editedPlaintextBody = editedTextBody.value || ''
    const hasMJML = !!originalMJMLBody
    const hasChanges = originalPlaintextBody !== editedPlaintextBody

    let improvedEmailBody = ''
    let editedMjmlBody = ''
    let editedHtmlBody = ''

    // If there are changes, call EditEmail to improve the email
    if (hasChanges && originalPlaintextBody && editedPlaintextBody) {
      // Only call EditEmail if we have content to improve and there are actual changes
      // Call EditEmail mutation - backend compiles MJML to HTML and handles retries
      try {
        const result = await editEmailMutation({
          input: {
            MJMLBody: originalMJMLBody || '', // Empty string if no MJML
            plaintextBody: originalPlaintextBody,
            editedplaintextBody: editedPlaintextBody,
            error: '',
            isMJML: hasMJML, // true if we have MJML, false for plaintext
          },
        })

        if (!result?.data?.editEmail) {
          // EditEmail returned empty/null - show error and stop
          showSnackbarMessage(
            'error',
            t('lead-timeline.email-activity.edit-email-error') ||
              'Failed to improve email. Please try again.'
          )
          return
        }

        // Backend returns compiled HTML when isMJML=true, plaintext otherwise
        // No need to compile MJML on frontend anymore
        editedHtmlBody = result.data.editEmail
        improvedEmailBody = editedHtmlBody
        // Note: We don't have the MJML anymore since backend compiles it
        // If we need to store editedMjmlBody, backend would need to return it separately
        editedMjmlBody = ''
      } catch (error) {
        // Mutation failed - Apollo handles retries for network errors
        Sentry.captureException(error, {
          tags: {
            component: 'EditEmailDialog',
            action: 'editEmailMutation',
          },
        })
        showSnackbarMessage('error', t('lead-timeline.email-activity.edit-email-please-try-later'))
        return
      }
    } else {
      // No changes, use original htmlBody (not mjmlBody, as UpdateNextMessage expects htmlBody)
      improvedEmailBody = props.message?.htmlBody || ''
      editedMjmlBody = props.message?.mjmlBody || ''
      editedHtmlBody = props.message?.htmlBody || ''
    }

    // Prepare attachments for GraphQL mutation
    // For existing attachments: include id, fileId, name, contentType, size (no content if not replacing)
    // For new attachments: include name, contentType, size, content (base64)
    // Convert File objects to Base64 only at submission time to reduce memory pressure
    const attachments = await Promise.all(
      editedAttachments.value.map(async (att) => {
        const attachmentInput = {
          id: att.id || null, // null for new attachments
          fileId: att.fileId || null, // null for new attachments
          name: att.name,
          contentType: att.contentType,
          size: att.size || 0, // File size in bytes
        }

        // For new attachments with File objects, convert to Base64 now
        if (att.file) {
          try {
            attachmentInput.content = await fileToBase64(att.file)
          } catch (error) {
            Sentry.captureException(error, {
              tags: {
                component: 'EditEmailDialog',
                action: 'convertFileToBase64OnSave',
              },
            })
            throw new Error(`Failed to read file "${att.name}"`)
          }
        } else if (att.content) {
          // For existing attachments with content already in Base64
          attachmentInput.content = att.content
        }

        return attachmentInput
      })
    )

    // Now call UpdateNextMessage with the improved email (only after EditEmail succeeds)
    const { data } = await updateNextMessageMutation({
      input: {
        contactId: props.contactId,
        subject: editedSubject.value,
        // Use improved email body from EditEmail (MJML when isMJML=true, plaintext when false)
        // If EditEmail wasn't called (no changes), use original htmlBody
        htmlBody: improvedEmailBody || props.message?.htmlBody || '',
        // Use edited text body content
        textBody: editedTextBody.value, // The final state of the text
        // Send attachments array (empty array clears all attachments, null preserves existing)
        // Send empty array if no attachments, otherwise send the array
        attachments: attachments.length > 0 ? attachments : [],
        runId: props.message?.runId || '',
        stepId: props.message?.stepId || '',
        automationId: props.message?.automationId || '',
        // Include edited HTML, MJML, and text bodies when EditEmail was called
        editedHtmlBody: editedHtmlBody || null,
        editedMjmlBody: editedMjmlBody || null,
        editedTextBody: editedTextBody.value || null,
      },
    })

    if (data?.updateNextMessage) {
      closeDialog()
      emit('saved', data.updateNextMessage)
      showSnackbarMessage(
        'success',
        t('lead-timeline.email-activity.message-updated') || 'Email updated successfully'
      )
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'EditEmailDialog',
        action: 'saveMessage',
      },
    })
    showSnackbarMessage(
      'error',
      t('lead-timeline.email-activity.update-error') || 'Failed to update email'
    )
  } finally {
    saving.value = false
  }
}

// Watch for changes to validate fields in real-time and update debounced size
watch(editedTextBody, () => {
  validateFields() // Update validation state in real-time
  updateDebouncedEmailBodySize() // Update email body size after debounce
})

// Watch for dialog open/close
watch(
  () => props.modelValue,
  async (newVal) => {
    if (newVal) {
      initializeDialog()
      // Load company data for metadata if not already loaded
      if (!companyResult.value && $auth?.user?.companyId) {
        await loadCompany()
      }
    } else {
      resetDialog()
    }
  }
)

// Watch mutation loading state
watch(mutationLoading, (loading) => {
  saving.value = loading
})
</script>

<style scoped>
.icon-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.dialog-header {
  flex-shrink: 0;
}

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.dialog-footer {
  flex-shrink: 0;
}
</style>
